import { DateTime } from 'luxon';
import Api from '@/modules/core/api/api.ts';
import { EntityLockEndPoint } from '@/modules/core/enums/core-end-point-enum.ts';
import type { TableId } from '@/modules/common/enums/table-id.enum.ts';
import type { SQLDate } from '@/modules/common/types/sql-date.type.ts';
import type { EntityLockDto } from '@/modules/core/dto/entity-lock.dto.ts';
import type { EntityLock } from '@/modules/core/types/entity-lock.type.ts';
import type { EntityLockRequest, EntityLockSetRequest } from '@/modules/core/requests/entity-lock.request.ts';

function toDateTime(value: SQLDate | null) {
  return value ? DateTime.fromSQL(value) : null;
}

export class EntityLockApi {
  static async get(tableId: TableId, id: number, signal?: AbortSignal): Promise<EntityLock> {
    const response: EntityLockDto = await Api.post(
      EntityLockEndPoint.Get,
      { parent_table_id: tableId, parent_id: id } satisfies EntityLockRequest,
      signal
    );

    return {
      isLocked: response.locked,
      userName: response.lock_info.user_fio,
      from: toDateTime(response.lock_info.blocked_date_from),
      to: toDateTime(response.lock_info.blocked_date_to),
    };
  }

  static async lock(tableId: TableId, id: number, expirationInSeconds?: number, signal?: AbortSignal) {
    const request: EntityLockSetRequest = { parent_table_id: tableId, parent_id: id };

    if (expirationInSeconds !== undefined) {
      request.expiration_in_seconds = expirationInSeconds;
    }

    await Api.post(EntityLockEndPoint.Set, { ...request }, signal);
  }

  static async unlock(tableId: TableId, id: number, signal?: AbortSignal) {
    await Api.post(
      EntityLockEndPoint.Unset,
      { parent_table_id: tableId, parent_id: id } satisfies EntityLockRequest,
      signal
    );
  }

  static unlockOnUnload(tableId: TableId, id: number) {
    return Api.beacon(EntityLockEndPoint.Unset, {
      parent_table_id: tableId,
      parent_id: id,
    } satisfies EntityLockRequest);
  }
}
